'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';

export type EditableInstrument = {
  id?: string;
  label: string;
  status: 'active' | 'inactive' | 'pending_details' | 'archived';
  isDefault: boolean;
  method: 'ach' | 'domesticWire' | 'internationalWire' | 'check' | 'unknown';
  currency: string;
  mercuryRecipientId?: string | null;
  mercuryRecipientName?: string | null;
  mercuryNickname?: string | null;
  notes?: string | null;
  bankDetails: {
    beneficiaryName?: string | null;
    bankName?: string | null;
    accountNumberLast4?: string | null;
    routingNumber?: string | null;
    accountType?: 'checking' | 'savings' | null;
    ibanLast4?: string | null;
    swiftCode?: string | null;
    country?: string | null;
  };
};

export function emptyInstrument(): EditableInstrument {
  return {
    label: '',
    status: 'active',
    isDefault: false,
    method: 'ach',
    currency: 'USD',
    mercuryRecipientId: '',
    mercuryRecipientName: '',
    mercuryNickname: '',
    notes: '',
    bankDetails: {
      beneficiaryName: '',
      bankName: '',
      accountNumberLast4: '',
      routingNumber: '',
      accountType: 'checking',
      ibanLast4: '',
      swiftCode: '',
      country: 'US',
    },
  };
}

type Props = {
  partner: string;
  open: boolean;
  initial: EditableInstrument;
  onClose: () => void;
  onSaved: () => void;
};

export function InstrumentEditorDialog({ partner, open, initial, onClose, onSaved }: Props) {
  const { t } = useTranslation('partnerPayouts');
  const { toast } = useToast();
  const isEdit = !!initial.id;
  const [form, setForm] = useState(initial);
  const [accountNumber, setAccountNumber] = useState('');
  const [iban, setIban] = useState('');

  const createMutation = trpc.partnerPaymentInstruments.create.useMutation({
    onSuccess: () => {
      toast({ title: t('opsSaved') });
      onSaved();
      onClose();
    },
    onError: (e) => toast({ title: t('opsSaveFailed'), description: e.message, variant: 'destructive' }),
  });
  const updateMutation = trpc.partnerPaymentInstruments.update.useMutation({
    onSuccess: () => {
      toast({ title: t('opsSaved') });
      onSaved();
      onClose();
    },
    onError: (e) => toast({ title: t('opsSaveFailed'), description: e.message, variant: 'destructive' }),
  });

  const pending = createMutation.isPending || updateMutation.isPending;
  const set = <K extends keyof EditableInstrument>(k: K, v: EditableInstrument[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const setBank = (k: keyof EditableInstrument['bankDetails'], v: string) =>
    setForm((f) => ({ ...f, bankDetails: { ...f.bankDetails, [k]: v } }));

  const submit = () => {
    if (!form.label.trim()) return;
    const bankDetails = {
      beneficiaryName: form.bankDetails.beneficiaryName || null,
      bankName: form.bankDetails.bankName || null,
      accountNumber: accountNumber.trim() || null,
      accountNumberLast4: form.bankDetails.accountNumberLast4 || null,
      routingNumber: form.bankDetails.routingNumber || null,
      accountType: form.bankDetails.accountType || null,
      iban: iban.trim() || null,
      ibanLast4: form.bankDetails.ibanLast4 || null,
      swiftCode: form.bankDetails.swiftCode || null,
      country: form.bankDetails.country || null,
    };
    const mercuryRecipientId = form.mercuryRecipientId?.trim() || null;
    const payload = {
      partner,
      label: form.label.trim(),
      status: (form.status === 'archived' ? 'active' : form.status) as EditableInstrument['status'],
      isDefault: form.isDefault,
      method: form.method,
      currency: form.currency.trim().toUpperCase() || 'USD',
      mercuryRecipientId,
      mercuryRecipientName: form.mercuryRecipientName?.trim() || null,
      mercuryNickname: form.mercuryNickname?.trim() || null,
      notes: form.notes?.trim() || null,
      bankDetails,
    };
    if (isEdit && form.id) {
      updateMutation.mutate({ ...payload, id: form.id });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('opsEditTitle') : t('opsAddTitle')}</DialogTitle>
          <DialogDescription>{t('opsEditDescription')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('colLabel')}</Label>
            <Input value={form.label} onChange={(e) => set('label', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('colStatus')}</Label>
            <Select
              value={form.status === 'archived' ? 'inactive' : form.status}
              onValueChange={(v) => set('status', v as EditableInstrument['status'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('status.active')}</SelectItem>
                <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
                <SelectItem value="pending_details">{t('status.pending_details')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('colMethod')}</Label>
            <Select
              value={form.method}
              onValueChange={(v) => set('method', v as EditableInstrument['method'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['ach', 'domesticWire', 'internationalWire', 'check', 'unknown'] as const).map((m) => (
                  <SelectItem key={m} value={m}>
                    {t(`method.${m}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('colCurrency')}</Label>
            <Input
              value={form.currency}
              maxLength={3}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
            />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => set('isDefault', e.target.checked)}
              />
              {t('default')}
            </label>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('colBeneficiary')}</Label>
            <Input
              value={form.bankDetails.beneficiaryName ?? ''}
              onChange={(e) => setBank('beneficiaryName', e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('colBank')}</Label>
            <Input
              value={form.bankDetails.bankName ?? ''}
              onChange={(e) => setBank('bankName', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('colRouting')}</Label>
            <Input
              value={form.bankDetails.routingNumber ?? ''}
              onChange={(e) => setBank('routingNumber', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('opsAccountLast4')}</Label>
            <Input
              value={form.bankDetails.accountNumberLast4 ?? ''}
              maxLength={4}
              onChange={(e) => setBank('accountNumberLast4', e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('opsAccountFull')}</Label>
            <Input
              value={accountNumber}
              placeholder={t('opsAccountFullPlaceholder')}
              onChange={(e) => setAccountNumber(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('opsIbanLast4')}</Label>
            <Input
              value={form.bankDetails.ibanLast4 ?? ''}
              maxLength={4}
              onChange={(e) => setBank('ibanLast4', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('opsSwift')}</Label>
            <Input
              value={form.bankDetails.swiftCode ?? ''}
              onChange={(e) => setBank('swiftCode', e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('opsIbanFull')}</Label>
            <Input
              value={iban}
              placeholder={t('opsIbanFullPlaceholder')}
              onChange={(e) => setIban(e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('opsMercuryId')}</Label>
            <Input
              value={form.mercuryRecipientId ?? ''}
              onChange={(e) => set('mercuryRecipientId', e.target.value)}
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('opsMercuryName')}</Label>
            <Input
              value={form.mercuryRecipientName ?? ''}
              onChange={(e) => set('mercuryRecipientName', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('opsMercuryNickname')}</Label>
            <Input
              value={form.mercuryNickname ?? ''}
              onChange={(e) => set('mercuryNickname', e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('opsNotes')}</Label>
            <Textarea value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            {t('opsCancel')}
          </Button>
          <Button onClick={submit} disabled={pending || !form.label.trim()}>
            {pending ? t('opsSaving') : t('opsSave')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
